/**
 * Read-Only Snapshot-Builder für die Solo-Simulation.
 * Liest nur — schreibt nie in clientData / appStore / Firestore.
 *
 * WHITELIST: Nur Sach-/Fixkosten ohne Personal. MA-Löhne/SV/Fremdlöhne = 0 in Solo-Basis.
 */
import {
  SOLO_DEFAULT_UMSATZ_BRUTTO,
  SOLO_DEFAULT_VAT,
  SOLO_DEFAULT_WOCHENSTUNDEN,
  SOLO_ORANGE_BWA_KEYS,
  type SoloCostLine,
  type SoloSimulationSnapshot,
} from '../store/soloSimulationStore';

/** Fixkosten-Whitelist (Solo-Basis, monatlich aus Periode / Zeitraum)
 *  Hinweis: Versicherungen/Beiträge sind umsatzgekoppelt → siehe SOLO_VARIABLE_BWA_KEYS
 */
const SOLO_FIX_BWA_KEYS = [
  'raumkosten',
  'betriebssteuern',
  'kfzKosten',
  'kredite',
  'zinsaufwendungen',
  'preisExpert',
  'premiumSoftware',
] as const;

const SOLO_FIX_LABELS: Record<(typeof SOLO_FIX_BWA_KEYS)[number], string> = {
  raumkosten: 'Raumkosten',
  betriebssteuern: 'Betriebliche Steuern',
  kfzKosten: 'KFZ Kosten',
  kredite: 'Kredite, Zinsen u.ä.',
  zinsaufwendungen: 'Zinsaufwendungen',
  preisExpert: 'PreisExpert Beratung',
  premiumSoftware: 'Premium Software',
};

/** Variable / umsatzgekoppelte Kosten (Orange + Versicherungen) */
const SOLO_VARIABLE_BWA_KEYS = SOLO_ORANGE_BWA_KEYS;

const SOLO_VARIABLE_LABELS: Record<(typeof SOLO_VARIABLE_BWA_KEYS)[number], string> = {
  versicherungen: 'Versicherungen/Beiträge (umsatzgekoppelt)',
  werbeReisekosten: 'Werbe/Reisekosten',
  reparatur: 'Reparatur/Instandhaltung',
  sonstigeKosten: 'sonstige Kosten',
};

const parseVal = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

export type SoloSnapshotLiveInput = {
  clientData: any;
  calculations: any;
  settings: any;
};

/**
 * Baut einen reinen Arbeitsspeicher-Snapshot aus der geöffneten Mandantenakte.
 */
export function buildSoloSnapshotFromLive({
  clientData,
  calculations,
  settings,
}: SoloSnapshotLiveInput): Omit<
  SoloSimulationSnapshot,
  'sourceMandantUid' | 'sourceYear' | 'capturedAt'
> {
  const bwa = clientData?.bwaData || {};
  const uData = clientData?.unternehmerData || {};
  const zeitraum = Math.max(1, parseVal(bwa.zeitraum) || parseVal(calculations?.zeitraum) || 12);

  const fixCostLines: SoloCostLine[] = [];
  let summeFixPeriode = 0;
  for (const key of SOLO_FIX_BWA_KEYS) {
    const periode = parseVal(bwa[key]);
    summeFixPeriode += periode;
    fixCostLines.push({
      key,
      label: SOLO_FIX_LABELS[key],
      monatNetto: zeitraum > 0 ? periode / zeitraum : 0,
    });
  }

  const variableCostLines: SoloCostLine[] = [];
  let summeVariablePeriode = 0;
  for (const key of SOLO_VARIABLE_BWA_KEYS) {
    const periode = parseVal(bwa[key]);
    summeVariablePeriode += periode;
    variableCostLines.push({
      key,
      label: SOLO_VARIABLE_LABELS[key],
      monatNetto: zeitraum > 0 ? periode / zeitraum : 0,
    });
  }

  const histUmsatzPeriode =
    parseVal(bwa.umsatz) || parseVal(calculations?.gesamtUmsatz) || 0;
  const historischerMonatsUmsatzNetto =
    parseVal(calculations?.monatsumsatzNetto) ||
    (zeitraum > 0 ? histUmsatzPeriode / zeitraum : 0);

  const summeVariableMonat = zeitraum > 0 ? summeVariablePeriode / zeitraum : 0;
  const baseFixkostenNetto = zeitraum > 0 ? summeFixPeriode / zeitraum : 0;

  const baseVariableKostenQuotePercent =
    historischerMonatsUmsatzNetto > 0
      ? (summeVariableMonat / historischerMonatsUmsatzNetto) * 100
      : 0;

  const baseMaterialQuotePercent = parseVal(calculations?.baseMaterialProzent);

  // Geräte = Fixbetrag Ø-Monat (BWA); Faktor nur für „Vorher“-Anzeige
  const baseGeraetekostenMonat = Math.max(
    0,
    parseVal(calculations?.geraetekostenMonat)
  );
  const baseGeraetekostenfaktor = parseVal(calculations?.geraetekostenfaktor);

  const baseSteuersatzPercent =
    parseVal(uData.einkommenssteuer) ||
    parseVal(calculations?.einkommenssteuerSatz) ||
    0;

  const baseGewerbesteuerBetrag =
    zeitraum > 0 ? parseVal(bwa.gewerbesteuer) / zeitraum : 0;

  const gewerbesteuerInBetrieblichenSteuern =
    !!bwa.gewerbesteuerInBetrieblichenSteuern;

  const prodGesamt = parseVal(calculations?.produktiveKostenGesamtMonat);
  const prodUl = parseVal(calculations?.prodUnternehmerlohn);
  const unprodMa = parseVal(calculations?.unproduktiveLohnkostenMitarbeiter);
  const basePersonalkostenIst = Math.max(0, prodGesamt - prodUl) + unprodMa;

  const mwstPercent =
    calculations?.mwstSatz !== undefined && calculations?.mwstSatz !== null
      ? parseVal(calculations.mwstSatz)
      : parseVal(settings?.country?.MwSt_Satz);
  const mwstFaktor =
    clientData?.isVatExempt || mwstPercent === 0
      ? 1
      : mwstPercent > 0
        ? 1 + mwstPercent / 100
        : SOLO_DEFAULT_VAT;

  // Prefills aus Unternehmerwerte-Seite (clientData.unternehmerData)
  const defaultMonatsUmsatzBrutto = SOLO_DEFAULT_UMSATZ_BRUTTO;

  const entnahme =
    parseVal(uData.entnahme) ||
    parseVal(uData.entnahmeNetto) ||
    parseVal(uData.unternehmerlohn);
  const sozial = parseVal(uData.sozialkosten);
  const defaultUnternehmerlohn =
    entnahme + sozial > 0 ? entnahme + sozial : entnahme > 0 ? entnahme : 2_500;

  const defaultWochenstunden =
    parseVal(uData.prodWochenstunden) > 0
      ? parseVal(uData.prodWochenstunden)
      : SOLO_DEFAULT_WOCHENSTUNDEN;

  const g = settings?.global || {};
  const lohnfortzahlungPercent = parseVal(g.Urlaubsausgleich) || 20;
  const verlustPercent = parseVal(g.Verlust_1) || 15;
  const wawi = parseVal(g.Warenwirtschaftsaufschlag_VK);
  const warenwirtschaftAufschlagVk = wawi > 0 ? wawi : 1.3;
  const liquiditaetPercent = parseVal(g.Liquiditaet) || 12.5;

  return {
    baseFixkostenNetto,
    baseVariableKostenQuotePercent,
    baseMaterialQuotePercent,
    baseGeraetekostenMonat,
    baseGeraetekostenfaktor,
    baseSteuersatzPercent,
    baseGewerbesteuerBetrag,
    basePersonalkostenIst,
    fixCostLines,
    variableCostLines,
    historischerMonatsUmsatzNetto,
    mwstFaktor,
    gewerbesteuerInBetrieblichenSteuern,
    defaultMonatsUmsatzBrutto,
    defaultUnternehmerlohn,
    defaultWochenstunden,
    lohnfortzahlungPercent,
    verlustPercent,
    warenwirtschaftAufschlagVk,
    liquiditaetPercent,
  };
}
